import { PImage } from "../../../../../shared/PImage/PImage";
import { useCategoryItemContext } from "../../../../../../services/context/CategoryItemContext";
import { LoyaltyOfferIcon } from "../../../../../global/LoyaltyOfferIcon/LoyaltyOfferIcon";
import {
  OfferGraphicCollage,
  useHasCollageGraphics,
} from "../../../../../global/OfferGraphicCollage/OfferGraphicCollage";
import { useCategoryOfferCard } from "../useCategoryOfferCard";
import { useCustomerAnalytics } from "../../../../../../services/hooks/useCustomerAnalytics";
import { useModalHook } from "../../../../../../services/hooks/useModal";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../../../../services/hooks/useTranslationI18";
import {
  setBottomSheetSource,
  setBottomSheetState,
} from "../../../../../../services/redux/slices/globalComponentsToggleSlice";
import { setSelectedItemTimeBoundData } from "../../../../../../services/redux/slices/itemStatusSlice";
import { TransformOfferType } from "../../../../../../services/redux/types/TypeHelper";
import CartItem from "../../../../../../utils/CartItem";
import { cn } from "../../../../../../utils/cn";
import {
  CUSTOMER_ANALYTICS_EVENT,
  ChangeCounterFrom,
  EditCartItemEnum,
} from "../../../../../../utils/enums";
import { isValidOffer } from "../../../../../../utils/help/offers/offers";
import { ItemOfferV1Type, OFFER_TYPE } from "../../../../../../utils/types";
import { CounterV2 } from "../../../../../global/Counter/CounterV2";
import { DiscountedPriceV2 } from "../../../../../global/DiscountedPrice/DiscountedPriceV2";
import { ItemName } from "../../../../../shared/ItemName/ItemName";
import { ItemTagChips } from "../../../../../shared/ItemTagChips/ItemTagChips";
import { CategoryItemText } from "../CategoryItemDetails/CategoryItemDetails";
import { endsWithGif } from "../CategoryItemDetails/CategoryItemDetailsHelper";
import { LabelV2 } from "../Label/LabelV2";
import { useCategoryItemCard } from "../useCategoryItemCard";
import styles from "./CategoryItemCardV2.module.scss";

export const CategoryItemCardV2Grid = () => {
  const { item, categoryId, index, addItem } = useCategoryItemContext();
  const offerCard = useCategoryOfferCard();
  const dispatch = useAppDispatch();
  const addCustomerAnalytics = useCustomerAnalytics();
  const { renderWebViewItem } = useModalHook();
  const { t } = useTranslationI18();

  const {
    itemDetails,
    isOutOfStock,
    cartCountExcludingOffers,
    cartItemIndexExcludingOffers,
    cartItemExcludingOffers,
    shouldOpenBottomSheet,
    canAddToCart,
    hideAddToCart,
    hidePrice,
    isMobile,
    isEcommerce,
  } = useCategoryItemCard();

  const offerGet = t("common:offer-get");
  const offerOff = t("common:offer-off");

  const handleCardClick = () => {
    if (shouldOpenBottomSheet) {
      dispatch(setBottomSheetSource("item_selection"));
      dispatch(setBottomSheetState(true));

      return;
    }

    if (!item || !itemDetails) {
      return;
    }

    dispatch(setSelectedItemTimeBoundData(item.timeBoundAvailabilityData));
    !isMobile && renderWebViewItem(itemDetails.id);
  };

  const handleAddClick = (e?: React.MouseEvent) => {
    if (isMobile) {
      e?.preventDefault();
    } else {
      e?.stopPropagation();
    }

    if (!canAddToCart) {
      return;
    }

    if (shouldOpenBottomSheet) {
      addCustomerAnalytics(
        CUSTOMER_ANALYTICS_EVENT.ADD_ITEM_ATTEMPT_BEFORE_BRANCH_AND_ORDER_TYPE_SELECTION
      );
      dispatch(setBottomSheetSource("item_selection"));
      dispatch(setBottomSheetState(true));

      return;
    }

    if (cartCountExcludingOffers > 0) {
      return;
    }

    if (!item) {
      return;
    }

    dispatch(setSelectedItemTimeBoundData(item.timeBoundAvailabilityData));
    addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.ADD_ITEM_FROM_MENU);
    addItem(e, index);
  };

  /* An offer whose items have all lost their photographs shows its badge
   * rather than an empty tile. */
  const hasCollage = useHasCollageGraphics(offerCard?.graphics);
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const isMainDark = restaurantInfo?.colors?.theme === "main_dark";

  // Same markup and classes as a menu item, so a "Specially for you" tile and
  // an ordinary tile are indistinguishable apart from their content.
  if (offerCard) {
    return (
      <div
        className={cn(styles.grid_item, isMainDark && "invert")}
        id={categoryId.toString()}
      >
        <div className={cn(styles.img_container, styles.offer_media)}>
          {hasCollage ? (
            <OfferGraphicCollage graphics={offerCard.graphics} />
          ) : (
            <span className={styles.offer_badge}>
              <LoyaltyOfferIcon
                offer={offerCard.offer}
                iconOnly
                backgroundIconColor="primary"
                width={48}
              />
            </span>
          )}
          <div className={styles.counter_overlay}>
            <CounterV2
              count={offerCard.isApplied ? 1 : 0}
              changeCounterView={ChangeCounterFrom.CartView}
              size="lg"
              layout="horizontal"
              hasShadow
              includeBackground
              isPlusDisabled={offerCard.isApplied}
              customOnClick={(editType) =>
                editType === EditCartItemEnum.CounterIncChange
                  ? offerCard.openOffer()
                  : offerCard.removeOffer()
              }
            />
          </div>
        </div>

        <div className={styles.grid_item__content}>
          <div
            className={styles.grid_item__text_content}
            data-testid="offer-grid-item"
          >
            <span className={styles.list_item__name}>{offerCard.title}</span>
          </div>
          {!hidePrice && (
            <div className={styles.list_item__price}>
              {offerCard.priceLabel}
              {offerCard.priceBeforeLabel && (
                <del className={styles.old_price}>
                  {offerCard.priceBeforeLabel}
                </del>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!item || !itemDetails) {
    return null;
  }

  return (
    <div
      onClick={handleCardClick}
      className={cn(styles.grid_item, isMainDark && "invert")}
      id={categoryId.toString()}
      key={itemDetails.img_url + index}
    >
      <div className={styles.img_container}>
        <PImage
          id={`next-img-${itemDetails.id}`}
          src={itemDetails.img_url}
          alt={itemDetails.item_name}
          fill
          style={{ objectFit: "cover", objectPosition: "center" }}
          unoptimized={endsWithGif(itemDetails.img_url)}
          quality="100"
          className={styles.img}
          sizes="(max-width: 900px) 33vw, (max-width: 1200px) 20vw"
        />
        <div className={styles.chips_row}>
          <LabelV2 />
          <ItemTagChips item={itemDetails} isOutOfStock={isOutOfStock} />
        </div>
        {!hideAddToCart && (
          <div
            className={styles.counter_overlay}
            onClick={(e) => handleAddClick(e)}
          >
            <CounterV2
              count={cartCountExcludingOffers}
              itemIndex={cartItemIndexExcludingOffers}
              data={cartItemExcludingOffers as TransformOfferType<CartItem>}
              changeCounterView={ChangeCounterFrom.CartView}
              isDisabled={!canAddToCart}
              size="lg"
              layout="horizontal"
              hasShadow
              includeBackground
            />
          </div>
        )}
      </div>

      <div className={styles.grid_item__content}>
        <div className={styles.grid_item__text_content}>
          <span
            className={styles.list_item__name}
            data-testid={`menu-item-${itemDetails.item_name}`}
          >
            <ItemName item={item} />
          </span>
          {isValidOffer(itemDetails?.offer as ItemOfferV1Type) && (
            <p className={styles.list_item__offer_text}>
              {offerGet} {itemDetails.offer?.offerValue}
              {itemDetails.offer?.offerType === OFFER_TYPE.PERCENTAGE
                ? "%"
                : ""}{" "}
              {offerOff}
            </p>
          )}
        </div>
        {!hidePrice && (
          <div className={styles.list_item__price}>
            <DiscountedPriceV2
              item={itemDetails}
              isValidOffer={isValidOffer(itemDetails.offer as ItemOfferV1Type)}
            />
          </div>
        )}
      </div>
      {isEcommerce && <CategoryItemText />}
    </div>
  );
};
